import React, { useMemo } from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Mini 30-day bar chart per KB: each column is a day, split into two
 * stacked segments — green = analyzed (score ≥ threshold), red = escalated.
 * Empty days render as a 1px muted floor so the axis feels continuous.
 */
export const KBSparkline = ({ data, loading, threshold }) => {
  const points = useMemo(() => data || [], [data]);
  const maxTotal = useMemo(
    () => Math.max(1, ...points.map(p => (p.analyzed || 0) + (p.escalated || 0))),
    [points],
  );
  const totals = useMemo(
    () => points.reduce((acc, p) => ({
      analyzed: acc.analyzed + (p.analyzed || 0),
      escalated: acc.escalated + (p.escalated || 0),
    }), { analyzed: 0, escalated: 0 }),
    [points],
  );
  const escRate = totals.analyzed + totals.escalated > 0
    ? Math.round((totals.escalated / (totals.analyzed + totals.escalated)) * 100)
    : 0;

  if (loading) {
    return (
      <div className="rounded-lg bg-white/5 border border-white/5 px-3 py-2 flex items-center justify-center text-white/30 text-[11px]">
        <Loader2 className="w-3 h-3 mr-2 animate-spin" />Loading 30-day activity…
      </div>
    );
  }

  if (totals.analyzed + totals.escalated === 0) {
    return (
      <div className="rounded-lg bg-white/5 border border-white/5 px-3 py-2 text-white/30 text-[11px]" data-testid="kb-sparkline-empty">
        No graded calls in the last 30 days.
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white/5 border border-white/5 px-3 py-2" data-testid="kb-sparkline">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-wider text-white/40">Last 30 days</div>
        <div className="flex items-center gap-2 text-[10px]">
          <span className="flex items-center gap-1 text-emerald-300"><span className="inline-block w-1.5 h-1.5 rounded-sm bg-emerald-400" />{totals.analyzed} analyzed</span>
          <span className="flex items-center gap-1 text-red-300"><span className="inline-block w-1.5 h-1.5 rounded-sm bg-red-400" />{totals.escalated} escalated</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] ${escRate >= 50 ? 'bg-red-500/15 text-red-300' : escRate >= 25 ? 'bg-amber-500/15 text-amber-300' : 'bg-emerald-500/15 text-emerald-300'}`}>
            {escRate}% esc.
          </span>
        </div>
      </div>
      <div className="flex items-end gap-[2px] h-10">
        {points.map((p, i) => {
          const total = (p.analyzed || 0) + (p.escalated || 0);
          const h = total === 0 ? 2 : Math.max(3, Math.round((total / maxTotal) * 40));
          const escFrac = total === 0 ? 0 : (p.escalated || 0) / total;
          const escH = Math.round(h * escFrac);
          const okH = h - escH;
          return (
            <div
              key={p.date || `spark-${i}`}
              className="flex-1 flex flex-col justify-end min-w-0"
              title={`${p.date}: ${p.analyzed} analyzed, ${p.escalated} escalated`}
              data-testid={`sparkline-day-${i}`}
            >
              {escH > 0 && <div style={{ height: `${escH}px` }} className="bg-red-400/80 rounded-sm" />}
              {okH > 0 && <div style={{ height: `${okH}px` }} className={total === 0 ? 'bg-white/10 rounded-sm' : 'bg-emerald-400/70 rounded-sm'} />}
            </div>
          );
        })}
      </div>
      <div className="mt-1 text-[9px] text-white/25">Threshold: {threshold}% · green = analyzed, red = escalated</div>
    </div>
  );
};
